import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import { environment } from "../environments/environment";
import { Observable } from "rxjs";
import { ConnectorService } from "./connector.service";
import { SwPush } from "@angular/service-worker";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NewsService } from "./news-service.service";
import { Capacitor } from '@capacitor/core';

export class InAppNotification {
    receiveImportantNotifications: boolean = false;
    receiveAssociativeNotifications: boolean = false;
    receivePronoNotifications: boolean = false;
    receiveSuggestionNotifications: boolean = false;
    receiveNewsletter: boolean = false;
    unreadDocs: number = 0;
    missingPronostics: number = 0;
    compteIssues: number = 0;
    socioIssues: number = 0;
    suggestionIssues: number = 0;
    consultationsAvailable: number = 0;
    news: string[] = [];
}

@Injectable({
    providedIn: 'root'
})
export class NotificationsService {
    public notifications: InAppNotification = new InAppNotification();
    notificationsObs: Observable<InAppNotification> = new Observable<InAppNotification>();
    private _snackBar = inject(MatSnackBar);
    public readonly VAPID_PUBLIC_KEY = "BBPqTsDtCtza5gTuo1nERE7iq4Bvpzy1qyvmGhEGeokhxpHhPrMgZNZg_c3jnAkIpo-IRlRfZS-_IqZkIFU5bVY";

    private pushNotifications: { title: string; body: string; timestamp: number; opened: boolean }[] = [];

    constructor(
        private http: HttpClient,
        private connector: ConnectorService,
        private swPush: SwPush,
        private newsService: NewsService
    )
    {
        if ('serviceWorker' in navigator && !Capacitor.isNativePlatform()) {
            navigator.serviceWorker.addEventListener('message', (event) => {
              if (event.data?.type === 'NOTIFICATION_RECEIVED' || event.data?.type === 'PUSH') {
                this.addPushNotification(event.data.data);
              }
            });
        }
    }

    getPushNotifications() {
        return this.pushNotifications;
    }

    async changeNotificationsReceive(){
        const toBeSent = {
            receiveImportantNotifications: this.notifications.receiveImportantNotifications,
            receiveAssociativeNotifications: this.notifications.receiveAssociativeNotifications,
            receivePronoNotifications: this.notifications.receivePronoNotifications,
            receiveSuggestionNotifications: this.notifications.receiveSuggestionNotifications,
            receiveNewsletter: this.notifications.receiveNewsletter
        };
        await this.http.post(environment.backAPI+'/notifications/notifications', toBeSent, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+ this.connector.token()
            }
        }).subscribe();
    }

    addPushNotification(notification: { title: string; body: string }) {
        this._snackBar.open(notification.title+' - '+notification.body, 'Fermer');
        this.pushNotifications.unshift({
            title: notification.title,
            body: notification.body,
            timestamp: Date.now(),
            opened: false
        });
    }
  
    async getInAppNotifications(){
        const _this = this;
        _this.notificationsObs = await this.http.get<InAppNotification>(environment.backAPI+'/inapp-notifications',{
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+ this.connector.token()
        }
        });
        _this.notificationsObs.subscribe(
        (result) => {
            if(result !== undefined && !(result instanceof HttpErrorResponse)){
                _this.notifications = result;
                _this.newsService.setNews(_this.notifications.news);
                return result;
            }
            else{
            this.connector.disconnection();
            return [];
            }
        },
        (error) => {
            this.connector.disconnection();
            return [];
        }
        );
    }

    subscribeToNotifications() {
      if (Capacitor.isNativePlatform()) {
        this.subscribeToFcmNotifications();
      } else {
        this.subscribeToWebPushNotifications();
      }
    }

    // Natif : les écouteurs FCM ne sont posés qu'une fois par lancement ; l'enregistrement,
    // lui, peut être relancé (bouton « Activer » après un refus)
    private fcmListenersReady = false;
    private fcmPending: ((ok: boolean) => void) | null = null;
    private static readonly FCM_FLAG = 'fcmRegistered';

    private async subscribeToFcmNotifications(): Promise<boolean> {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== 'granted') {
          console.log('Push notifications permission denied');
          return false;
        }
        await this.ensureFcmListeners(PushNotifications);
        // Résultat de l'enregistrement : jeton reçu et envoyé au back, ou erreur (10 s au plus)
        const done = new Promise<boolean>((resolve) => {
          this.fcmPending = resolve;
          setTimeout(() => { if (this.fcmPending === resolve) { this.fcmPending = null; resolve(false); } }, 10000);
        });
        await PushNotifications.register();
        return await done;
      } catch (e) {
        console.error('Erreur initialisation push natif:', e);
        return false;
      }
    }

    private async ensureFcmListeners(PushNotifications: any): Promise<void> {
      if (this.fcmListenersReady) return;
      this.fcmListenersReady = true;
      const settle = (ok: boolean) => { const pending = this.fcmPending; this.fcmPending = null; pending?.(ok); };

      await PushNotifications.addListener('registration', (token: { value: string }) => {
        this.http.post(environment.backAPI + '/notifications/fcm-token',
          { token: token.value },
          { headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + this.connector.token() } }
        ).subscribe({
          next: () => {
            try { localStorage.setItem(NotificationsService.FCM_FLAG, '1'); } catch { /* ignoré */ }
            settle(true);
          },
          error: (err) => { console.error('Erreur enregistrement FCM token:', err); settle(false); }
        });
      });
      await PushNotifications.addListener('registrationError', (err: any) => {
        console.error('FCM registration error:', err);
        settle(false);
      });
      // Notification reçue app au premier plan
      await PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
        this.addPushNotification({ title: notification.title || '', body: notification.body || '' });
      });
      await PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
        console.log('Notification action:', action);
      });
    }

    private async subscribeToWebPushNotifications(): Promise<boolean> {
      try {
        const subscription = await this.swPush.requestSubscription({ serverPublicKey: this.VAPID_PUBLIC_KEY });
        const userId = this.connector.getId().toString();
        if (!userId || userId === '0') {
          console.error('Impossible de s\'abonner : utilisateur non connecté');
          return false;
        }
        // Jeton de sécurité : SHA-256 de userId + endpoint
        const token = await this.generateSecurityToken(userId, subscription.endpoint);
        const payload = { ...subscription.toJSON(), userId, token };
        await new Promise<void>((resolve, reject) =>
          this.http.post(environment.backAPI + '/notifications/subscription', payload, {
            headers: { 'Authorization': 'Bearer ' + this.connector.token() }
          }).subscribe({ next: () => resolve(), error: reject }));
        return true;
      } catch (err) {
        console.error('Échec de l\'abonnement aux notifications', err);
        return false;
      }
    }

    /**
     * Bouton « Activer » des préférences : demande l'autorisation, abonne cet appareil et
     * renvoie un message à afficher. Quand l'appareil bloque les notifications (refus
     * mémorisé, iPhone hors appli installée…), explique comment les débloquer.
     */
    async activateNotifications(): Promise<{ ok: boolean; message: string }> {
      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const current = await PushNotifications.checkPermissions().catch(() => ({ receive: 'prompt' as const }));
        if (current.receive === 'denied') {
          return { ok: false, message: 'Les notifications sont bloquées pour MySociochaux sur ce téléphone. Autorisez-les dans Paramètres › Applications › MySociochaux › Notifications, puis revenez ici.' };
        }
        const ok = await this.subscribeToFcmNotifications();
        return ok
          ? { ok, message: 'Notifications activées sur ce téléphone 🎉' }
          : { ok, message: 'Activation impossible pour le moment. Vérifiez votre connexion et que les notifications sont autorisées pour MySociochaux dans les paramètres du téléphone.' };
      }

      const iOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
        return {
          ok: false,
          message: iOS
            ? 'Sur iPhone, les notifications ne marchent que depuis l\'appli installée : dans Safari, touchez Partager › Sur l\'écran d\'accueil, puis ouvrez MySociochaux depuis l\'icône.'
            : 'Ce navigateur ne permet pas les notifications. Essayez avec Chrome, Edge ou Firefox.',
        };
      }
      if (!this.swPush.isEnabled) {
        return { ok: false, message: 'Les notifications ne sont pas disponibles ici (navigation privée ou navigateur trop ancien). Ouvrez MySociochaux dans une fenêtre normale.' };
      }
      if (Notification.permission === 'denied') {
        return { ok: false, message: 'Votre navigateur bloque les notifications de MySociochaux. Cliquez sur le cadenas à gauche de l\'adresse › Notifications › Autoriser, puis rechargez la page.' };
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return { ok: false, message: 'Notifications non autorisées. Vous pourrez les activer plus tard depuis cet écran.' };
      }
      const ok = await this.subscribeToWebPushNotifications();
      return ok
        ? { ok, message: 'Notifications activées sur cet appareil 🎉' }
        : { ok, message: 'Autorisation accordée mais abonnement impossible. Rechargez la page et réessayez.' };
    }

    // Générer le token de sécurité (SHA256)
    private async generateSecurityToken(userId: string, endpoint: string): Promise<string> {
      const encoder = new TextEncoder();
      const data = encoder.encode(userId + endpoint);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    
    isSubscribedToNotifications() {
        if (Capacitor.isNativePlatform()) return this.isFcmRegistered();
        // L'API Notification n'existe pas partout (WebView Android, anciens navigateurs)
        return typeof Notification !== 'undefined' && Notification.permission === 'granted';
    }

    private isFcmRegistered(): boolean {
        try { return localStorage.getItem(NotificationsService.FCM_FLAG) === '1'; } catch { return false; }
    }

    isThisDeviceSubscribedToNotifications() {
        if (Capacitor.isNativePlatform()) return this.isFcmRegistered();
        return this.swPush.isEnabled && this.swPush.subscription !== null;
    }

    atLeastOneNotificationChecked(){
        return this.notifications.receiveImportantNotifications || this.notifications.receiveAssociativeNotifications || this.notifications.receivePronoNotifications;
    }

    requestNotificationPermission(): Promise<NotificationPermission> {
        // Appli Android/iOS : permission native (l'API Notification du navigateur n'existe
        // pas dans la WebView Android — son appel cassait l'initialisation de l'accueil)
        if (Capacitor.isNativePlatform()) {
            return import('@capacitor/push-notifications')
                .then(({ PushNotifications }) => PushNotifications.requestPermissions())
                .then(result => {
                    const permission: NotificationPermission = result.receive === 'granted' ? 'granted'
                        : result.receive === 'denied' ? 'denied' : 'default';
                    if (permission === 'granted') this.subscribeToNotifications();
                    return permission;
                })
                .catch(() => 'default' as NotificationPermission);
        }
        if (typeof Notification === 'undefined') {
            return Promise.resolve('denied' as NotificationPermission);
        }
        return Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
                this.subscribeToNotifications();
            } else if (permission === 'denied') {
                console.error('Permission de notification refusée');
            } else {
                console.error('Permission de notification ignorée');
            }
            return permission;
        });
    }

    showNotification(title: string, body: string) {
        if (this.isSubscribedToNotifications()) {
            this._snackBar.open(title+' - '+body, 'Fermer', {
                duration: 5000,
                verticalPosition: 'top',
        horizontalPosition: 'right',
                panelClass: ['notification-snackbar']
            });
        } else {
            console.error('Notifications non activées');
        }
    }
}
